import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { SERVICES } from '../constants';
import { serviceData } from '../constants/services';

export const SERVICE_HIGHLIGHTS = {
  1: ['React and Next.js', 'Sub-second page loads', 'Core Web Vitals tuned'],
  2: ['Custom LLM integration', 'Workflow automation', 'AI agents that work around the clock'],
  3: ['Flutter and React Native', 'iOS and Android from one codebase', 'Offline-first sync'],
  4: ['APIs and microservices', 'Database architecture', 'Cloud and DevOps'],
  5: ['Multi-tenant MVPs', 'Billing and subscriptions', 'Role-based access'],
};

export function SectionHead({ id, title, children, className = '' }) {
  return (
    <div className={`grid gap-6 lg:grid-cols-12 lg:items-end ${className}`}>
      <h2 id={id} className="h-section lg:col-span-7">
        {title}
      </h2>
      {children && <div className="lede lg:col-span-5 lg:justify-self-end">{children}</div>}
    </div>
  );
}

const slugOf = (link) => link.split('/').pop();

export default function Services({ isPreview = false, hideHead = false }) {
  const [active, setActive] = useState(0);
  const blocksRef = useRef([]);


  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(Number(e.target.dataset.index));
        });
      },
      { rootMargin: '-45% 0px -45% 0px' }
    );
    blocksRef.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, []);

  const current = SERVICES[active];
  const tech = serviceData[slugOf(current.link)]?.technologies || [];

  return (
    <section
      id="services"
      aria-labelledby="services-title"
      className={`theme-dark sky-noon relative overflow-clip ${hideHead ? 'pt-8 pb-24 sm:pb-32' : 'py-24 sm:py-32'}`}
    >
      <div className="frame relative">
        {hideHead ? (
          <h2 id="services-title" className="sr-only">
            Our services
          </h2>
        ) : (
          <SectionHead id="services-title" title="What we build">
Five ways we solve it.
          </SectionHead>
        )}

        <div className="relative mt-16 lg:grid lg:grid-cols-12 lg:gap-10">
          <ol className="lg:col-span-12">
            {SERVICES.map((s, i) => {
              const tags = serviceData[slugOf(s.link)]?.technologies || [];
              return (
                <li
                  key={s.id}
                  ref={(el) => (blocksRef.current[i] = el)}
                  data-index={i}
                  className="relative border-t border-line py-10 lg:flex lg:min-h-[62vh] lg:items-center lg:py-0"
                >
                  {/* scenery for the glass panel: this service's stack, drifting behind it */}
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute right-0 top-1/2 hidden w-[44%] -translate-y-1/2 select-none text-right lg:block"
                  >
                    {tags.slice(0, 4).map((t, k) => (
                      <span
                        key={t}
                        className={`display block text-[5.2rem] leading-[0.95] ${
                          k % 2 ? 'text-accent/85' : 'text-white/85'
                        }`}
                      >
                        {t}
                      </span>
                    ))}
                  </div>

                  <div className="relative lg:w-[52%]">
                    <h3 className={`display text-[2.4rem] sm:text-6xl lg:text-[4.25rem] transition-opacity duration-500 ${active === i ? 'opacity-100' : 'opacity-40'}`}>
                      <Link to={s.link} className="hover:text-accent transition-colors">
                        {s.title}
                      </Link>
                    </h3>
                    <p className="mt-5 max-w-[34rem] text-lg text-muted">{s.description}</p>

                    {/* phones and tablets: details inline */}
                    <div className={`glass-css mt-8 p-6 transition-all duration-700 ease-out lg:hidden ${active === i ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-4 scale-[0.97] opacity-50'}`}>
                      <ul className="space-y-2 text-white">
                        {(SERVICE_HIGHLIGHTS[s.id] || []).map((h) => (
                          <li key={h} className="flex gap-3">
                            <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                            {h}
                          </li>
                        ))}
                      </ul>
                      <p className="mt-5 text-sm text-muted">{tags.join(', ')}</p>
                      <Link to={s.link} className="mt-6 inline-flex items-center gap-2 font-semibold text-white">
                        Explore this service <ArrowUpRight size={18} aria-hidden="true" />
                      </Link>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>

          {/* desktop: one sticky glass panel describing the service in view */}
          <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-[40%] lg:block">
            <div className="sticky top-[calc(50vh-15rem)] pt-4">
              <aside
                data-glass="service-panel"
                aria-live="polite"
                className="glass-pro glass-clear pointer-events-auto relative z-[25] flex h-[30rem] flex-col justify-between rounded-[2.25rem] p-9"
              >
                <div className="on-glass">
                  <p className="text-sm text-white/85">
                    {active + 1} of {SERVICES.length}
                  </p>
                  <p className="display-semi mt-3 text-3xl text-white">{current.title}</p>
                  <ul className="mt-7 space-y-3 text-white">
                    {(SERVICE_HIGHLIGHTS[current.id] || []).map((h) => (
                      <li key={h} className="flex gap-3">
                        <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#4FB0E7]" />
                        {h}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-6 text-sm text-white/85">{tech.join(', ')}</p>
                </div>
                <Link
                  to={current.link}
                  className="on-glass pointer-events-auto inline-flex h-12 w-fit items-center gap-2 rounded-full bg-white px-5 text-sm font-semibold text-[#07101F] transition-colors hover:bg-[#4FB0E7]"
                >
                  Explore {current.title.toLowerCase()}
                  <ArrowUpRight size={16} aria-hidden="true" />
                </Link>
              </aside>
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-col gap-3 sm:flex-row">
          <Link to="/contact" className="btn bg-white text-[#07101F] hover:bg-accent">
            {isPreview ? 'Get a free consultation' : 'Start a project'}
          </Link>
          {isPreview && (
            <Link to="/services" className="btn border border-white/30 text-white hover:border-white hover:bg-white/10">
              Compare all services
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}

